import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilterChipData {
  /** Facet id, e.g. `stage`. */
  id: string;
  /** Selected value; absent when the whole facet is one chip. */
  value?: string;
  /** What the facet is called: "Etapa". */
  label: string;
  /** What was chosen: "Calificado". */
  display: string;
}

export interface FilterChipProps extends FilterChipData {
  onRemove: (chip: FilterChipData) => void;
  className?: string;
}

export function FilterChip({ onRemove, className, ...chip }: FilterChipProps) {
  return (
    <span
      className={cn(
        'inline-flex h-7 max-w-64 items-center gap-1.5 rounded-md border border-border bg-muted/60 pl-2 pr-1 text-xs',
        className,
      )}
    >
      <span className="text-muted-foreground">{chip.label}:</span>
      <span className="truncate font-medium">{chip.display}</span>
      <button
        type="button"
        aria-label={`Quitar filtro ${chip.label}: ${chip.display}`}
        onClick={() => onRemove(chip)}
        className="grid size-4 shrink-0 place-items-center rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:bg-background hover:text-foreground"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

export interface FilterChipsProps {
  chips: readonly FilterChipData[];
  onRemove: (chip: FilterChipData) => void;
  className?: string;
}

/** The row of removable chips under a toolbar: what is filtering, in words. */
export function FilterChips({ chips, onRemove, className }: FilterChipsProps) {
  if (chips.length === 0) return null;

  return (
    <div
      role="list"
      aria-label="Filtros activos"
      className={cn('flex flex-wrap items-center gap-1.5', className)}
    >
      {chips.map((chip) => (
        <span role="listitem" key={`${chip.id}:${chip.value ?? ''}`}>
          <FilterChip {...chip} onRemove={onRemove} />
        </span>
      ))}
    </div>
  );
}
